import { Component, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useUserStore } from '../../stores'
import { routeNameFor, CRASH_CATEGORY } from '../../utils/bugReport'
import { openLinkOnDevice } from '../../utils/dataExport'
import ReportProblemModal from './ReportProblemModal'

/**
 * CrashFallback — REP-10 PA-4, Alex Q-REP3/Q-REP4 (2026-10-07, "as recommended").
 *
 * What ErrorBoundary renders while REPORT_PROBLEM_ENABLED is on: a translated plain sentence
 * (the error text and both stacks go to the console only, which componentDidCatch already
 * does), Try again / Go home, and "Report this problem" — the in-app form for a signed-in
 * patient, the website's patient support form for anyone else (the in-app route needs a
 * patient token).
 *
 * ★★★ THE REPORT NEVER CARRIES THE ERROR. Not its message, not either stack: both can hold
 *   patient data (a thrown value can be anything the screen held). This component is not even
 *   given the error — ErrorBoundary passes it the two buttons' handlers and the legacy screen,
 *   nothing else — so it cannot put it in a ticket. The report carries the crashed screen's
 *   route TEMPLATE and origin 'crash' only. check-report-problem.mjs arm K.
 * ★★ UNDER A LANGUAGE WITHOUT TEXT FOR THESE KEYS THE LEGACY SCREEN IS SHOWN. Since PROD-1156
 *   the legacy screen carries no error text outside a dev build (ErrorBoundary.renderLegacy).
 *   es has the crash.* text since PROD-1159 (our usted translation, Dr. Rami reviews after); the
 *   report form's support.* keys are still withheld there, so es offers the website form.
 * ★★ IF THIS COMPONENT ITSELF THROWS, THE LEGACY SCREEN IS SHOWN (FallbackGuard below). A crash
 *   screen that can crash is worse than the one it replaces.
 */
export const PATIENT_SUPPORT_WEB_URL = 'https://orellahealth.com/support/patient'

export class FallbackGuard extends Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error) {
    console.error('[CrashFallback] fallback failed; showing the legacy crash screen:', error?.name)
  }

  render() {
    if (this.state.failed) return this.props.legacy
    return this.props.children
  }
}

export default function CrashFallback({ legacy, onRetry, onHome }) {
  const { t, hasText } = useI18n()
  const isAuthenticated = useUserStore((state) => state.isAuthenticated)
  const location = useLocation()
  const [reporting, setReporting] = useState(false)
  // ★ The TEMPLATE of the crashed screen, computed from the pathname and nothing else.
  const [pattern] = useState(() => routeNameFor(location?.pathname))

  if (!['crash.title', 'crash.body', 'crash.report', 'crash.reportSignedOut'].every((k) => hasText(k))) return legacy
  // PROD-1159: the crash.* Spanish shipped (our translation, Dr. Rami reviews after) while the
  // report form's support.* keys are still withheld under es. A language without the form's
  // text offers the website form instead, never an English form behind a Spanish screen.
  const inApp = isAuthenticated && hasText('support.modalTitle')

  const openWebsite = () => {
    openLinkOnDevice(PATIENT_SUPPORT_WEB_URL).catch(() => {})
  }

  const button = {
    border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  }

  return (
    <div style={{ padding: 24, fontFamily: 'Inter, system-ui, sans-serif', maxWidth: 600, margin: '40px auto' }}>
      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: 20 }}>
        <h2 style={{ color: '#991B1B', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{t('crash.title')}</h2>
        <p style={{ color: '#B91C1C', fontSize: 13, marginBottom: 16 }}>{t('crash.body')}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={onRetry} style={{ ...button, background: '#46A8F9', color: 'white' }}>
            {t('crash.tryAgain')}
          </button>
          <button onClick={onHome} style={{ ...button, background: '#E2E8F0', color: '#334155' }}>
            {t('crash.home')}
          </button>
          {inApp ? (
            <button onClick={() => setReporting(true)} style={{ ...button, background: 'white', color: '#991B1B', border: '1px solid #FECACA' }}>
              {t('crash.report')}
            </button>
          ) : (
            <button onClick={openWebsite} style={{ ...button, background: 'white', color: '#991B1B', border: '1px solid #FECACA' }}>
              {t('crash.reportSignedOut')}
            </button>
          )}
        </div>
      </div>
      {reporting && inApp && (
        <ReportProblemModal
          origin="crash"
          presetCategory={CRASH_CATEGORY}
          routePattern={pattern}
          onClose={() => setReporting(false)}
        />
      )}
    </div>
  )
}
